import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { RootState } from '../../lib/store/index';
import { OpenPosition } from '../../lib/store/reporting/types';

import ClickableDiv from '../ClickableDiv/ClickableDiv';
import { InputControl } from '../index';

import styles from './SharesCalculator.module.scss';

type Props = {
  symbol: string;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  disabled?: boolean;
  maxQuantity?: number;
  clearNumberOfSharesError?: Dispatch<SetStateAction<string>>;
  isBuyOrder?: boolean;
}

const SharesCalculator = ({
  quantity, setQuantity, clearNumberOfSharesError, disabled, maxQuantity, isBuyOrder, symbol,
}: Props) => {
  const positions = useSelector<RootState, OpenPosition[]>(
    (state: RootState) => state.reporting.openPositions,
  );

  const isShortPosition = positions.filter((position) =>
    position.symbol === symbol && position.quantity < 0).length > 0;

  const reduceValue = () => {
    clearNumberOfSharesError && clearNumberOfSharesError('');
    if (quantity > 1) {
      setQuantity((currentQuantity: number) => currentQuantity - 1);
    }
  };

  const addValue = () => {
    clearNumberOfSharesError && clearNumberOfSharesError('');
    if (maxQuantity) {
      if (quantity < maxQuantity
        || (isBuyOrder && !isShortPosition)
        || (isShortPosition && quantity < Math.abs(maxQuantity))
        || (!isBuyOrder && isShortPosition)
      ) {
        setQuantity((currentQuantity: number) => currentQuantity + 1);
      }
    } else {
      setQuantity((currentQuantity: number) => currentQuantity + 1);
    }
  };

  return (
    <>
      <div className={classNames(styles.calculationContainer, { [styles.disabled]: disabled })}>
        <ClickableDiv className={styles.calculationButton} onClick={reduceValue}>
          <span>-</span>
        </ClickableDiv>
        <div className={styles.resultFieldWrapper}>
          <InputControl
            className={styles.resultFieldInput}
            type="number"
            placeholder=""
            withMinimumValue
            name="quantity"
            value={quantity}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              let newValue = parseInt(event.target.value, 10);

              if (isNaN(newValue) || newValue < 0) {
                newValue = 0; // Ensure the value never goes below 0
              }

              if (maxQuantity && newValue > maxQuantity) {
                newValue = maxQuantity;
              }

              setQuantity(newValue);
              clearNumberOfSharesError && clearNumberOfSharesError('');
            }}
          />
        </div>
        <ClickableDiv
          className={[styles.calculationButton, styles.calculationButtonRight].join(' ')}
          onClick={addValue}
        >
          <span>+</span>
        </ClickableDiv>
      </div>
    </>
  );
};

export default SharesCalculator;
